import { useEffect, useState } from "react";

/**
 * Floating "Install" pill — appears when the browser fires `beforeinstallprompt`.
 * On iOS Safari, fallback to an instructional hint (since iOS has no programmatic prompt).
 */
export default function InstallPWAButton() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(
    typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(display-mode: standalone)").matches
  );
  const [showIosHint, setShowIosHint] = useState(false);
  const [dismissed, setDismissed] = useState(
    typeof window !== "undefined" && localStorage.getItem("pwa_dismissed") === "1"
  );

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const isIos =
    typeof navigator !== "undefined" &&
    /iphone|ipad|ipod/i.test(navigator.userAgent || "") &&
    !/crios|fxios/i.test(navigator.userAgent || "");

  if (installed || dismissed) return null;
  // Hide if neither a deferred prompt nor iOS detected
  if (!deferred && !isIos) return null;

  const dismiss = () => {
    localStorage.setItem("pwa_dismissed", "1");
    setDismissed(true);
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      try {
        await deferred.userChoice;
      } catch (_) {}
      setDeferred(null);
    } else if (isIos) {
      setShowIosHint(true);
    }
  };

  return (
    <>
      <div
        data-testid="pwa-install-pill"
        style={{
          position: "fixed",
          bottom: "max(16px, env(safe-area-inset-bottom))",
          insetInlineStart: 16,
          zIndex: 50,
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 14px",
          borderRadius: 999,
          background:
            "linear-gradient(135deg, rgba(28,24,56,.92), rgba(38,30,80,.92))",
          backdropFilter: "blur(12px)",
          border: "1px solid var(--border-strong)",
          boxShadow: "0 14px 30px -10px rgba(0,0,0,0.6)",
        }}
      >
        <button
          data-testid="pwa-install-btn"
          className="btn btn-gold"
          onClick={install}
          style={{ padding: "8px 14px", fontSize: 13 }}
        >
          📲 تثبيت التطبيق
        </button>
        <button
          data-testid="pwa-dismiss-btn"
          onClick={dismiss}
          aria-label="إخفاء"
          style={{
            background: "transparent",
            border: 0,
            color: "var(--text-muted)",
            cursor: "pointer",
            fontSize: 16,
            padding: 4,
          }}
        >
          ✕
        </button>
      </div>

      {showIosHint && (
        <div
          data-testid="pwa-ios-hint"
          onClick={() => setShowIosHint(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(8,6,20,0.85)",
            display: "grid",
            placeItems: "center",
            zIndex: 70,
            padding: 18,
          }}
        >
          <div
            className="panel"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 380, textAlign: "center" }}
          >
            <div style={{ fontSize: 48, marginBottom: 8 }}>📱</div>
            <h3 style={{ margin: "0 0 8px" }}>ثبّت بَلاغة على iPhone</h3>
            <p className="dim" style={{ lineHeight: 1.9 }}>
              من شريط Safari السفلي:
              <br />
              ١. اضغط <strong style={{ color: "var(--gold)" }}>أيقونة المشاركة</strong> ⬆️
              <br />
              ٢. اختر <strong style={{ color: "var(--gold)" }}>«إضافة إلى الشاشة الرئيسية»</strong>
              <br />
              ٣. اضغط <strong style={{ color: "var(--gold)" }}>«إضافة»</strong>
            </p>
            <button
              className="btn btn-primary"
              onClick={() => setShowIosHint(false)}
              style={{ marginTop: 10 }}
            >
              فهمت
            </button>
          </div>
        </div>
      )}
    </>
  );
}
